import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

// The nutrition tab's simple (non-copy) dialogs: meal note, plan-delete, meal-plan-delete, food-item-delete.
const NutritionDialogs = ({
  mealNoteDialog,
  mealNoteDraft,
  setMealNoteDraft,
  mealNoteError,
  mealNoteSaving,
  handleMealNoteClose,
  handleMealNoteSave,
  planDeleteDialog,
  setPlanDeleteDialog,
  planDeleteLoading,
  handlePlanDeleteConfirm,
  mealPlanDeleteDialog,
  setMealPlanDeleteDialog,
  mealPlanDeleteLoading,
  handleConfirmMealPlanDelete,
  mealDeleteDialog,
  setMealDeleteDialog,
  mealDeleteLoading,
  handleConfirmMealDelete,
}) => (
  <>
    <Dialog open={!!mealNoteDialog} onOpenChange={(open) => (!open ? handleMealNoteClose() : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mealNoteDialog?.item?.name ? `Note for ${mealNoteDialog.item.name}` : 'Meal item note'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Add or edit a note for this meal item.</p>
          <textarea
            value={mealNoteDraft}
            onChange={(e) => setMealNoteDraft(e.target.value)}
            placeholder="Type your note here..."
            rows={5}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-fitness-primary focus:outline-none"
          />
          {mealNoteError && <p className="text-sm text-red-600">{mealNoteError}</p>}
        </div>
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={handleMealNoteClose}
            disabled={mealNoteSaving}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleMealNoteSave}
            disabled={mealNoteSaving || !mealNoteDialog?.item?.mealfoodId || !mealNoteDialog?.item?.mealId}
            className="inline-flex items-center justify-center rounded-md bg-fitness-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-fitness-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {mealNoteSaving ? 'Saving...' : 'Save'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!planDeleteDialog} onOpenChange={(open) => (!open ? setPlanDeleteDialog(null) : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete meal plan?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This will remove the meal plan and its items. This action can’t be undone.</p>
          {planDeleteDialog && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{planDeleteDialog.name}</div>
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={() => setPlanDeleteDialog(null)}
            disabled={planDeleteLoading}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePlanDeleteConfirm}
            disabled={planDeleteLoading}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {planDeleteLoading ? 'Deleting...' : 'Delete'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!mealPlanDeleteDialog} onOpenChange={(open) => (!open ? setMealPlanDeleteDialog(null) : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete meal?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This will remove the entire meal from the meal plan. This action can’t be undone.</p>
          {mealPlanDeleteDialog?.meal && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{mealPlanDeleteDialog.meal.title || 'Meal'}</div>
              {mealPlanDeleteDialog.plan?.name && (
                <div className="text-xs text-slate-500">Plan: {mealPlanDeleteDialog.plan.name}</div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={() => setMealPlanDeleteDialog(null)}
            disabled={mealPlanDeleteLoading}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmMealPlanDelete}
            disabled={mealPlanDeleteLoading}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {mealPlanDeleteLoading ? 'Deleting...' : 'Delete'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!mealDeleteDialog} onOpenChange={(open) => (!open ? setMealDeleteDialog(null) : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete food item?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            This will remove the item from{mealDeleteDialog?.mealTitle ? ` ${mealDeleteDialog.mealTitle}` : ' this meal plan'}. This action can’t be undone.
          </p>
          {mealDeleteDialog?.item && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{mealDeleteDialog.item.name}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold text-slate-500">
                <span className="rounded-full bg-white px-2 py-0.5">Cals {mealDeleteDialog.item.calories ?? 0}</span>
                <span className="rounded-full bg-white px-2 py-0.5">P {mealDeleteDialog.item.protein ?? 0}g</span>
                <span className="rounded-full bg-white px-2 py-0.5">C {mealDeleteDialog.item.carbs ?? 0}g</span>
                <span className="rounded-full bg-white px-2 py-0.5">F {mealDeleteDialog.item.fat ?? 0}g</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={() => setMealDeleteDialog(null)}
            disabled={mealDeleteLoading}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmMealDelete}
            disabled={mealDeleteLoading}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {mealDeleteLoading ? 'Deleting...' : 'Delete'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
)

export default NutritionDialogs
